/* eslint-disable */
// NotificationDrawer — the panel behind the megaphone. Opened from the
// notifications store; mounts through AppOverlays, above the router, so it
// survives navigation exactly as the card does.
//
// ⚠️ OPENING IS NOT READING (spec §2.2). On open this stamps `seen_at` on the
// rows it renders and NOTHING else. The badge must not move. A badge that clears
// because a panel opened teaches people the number is decorative — read_at is set
// by exactly three things: clicking a row, Mark all read, and dismissing a card.
//
// ⚠️ The badge is still fed by useNotifications' single array. This drawer has its
// own query (history: read rows, older rows, paged) and is NOT the badge's source.
// They agree because every mutation invalidates both keys — not because either
// reads the other.
//
// Motion mirrors the WORKSPACE drawer (CatalogDrawer) so the app's two drawers
// behave identically. Mirrored, not imported — that component is entangled with
// workspace layout.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../supabase';
import { BriefingEntry } from './briefing/BriefingEntry';
import { useNotificationsStore } from '../../stores/notificationsStore';
import { useNotificationHistory } from '../../hooks/useNotificationHistory';
import { NotificationRow, ArchivedUndoRow } from './NotificationRow';
import styles from './notificationDrawer.module.css';

// 8s, not 5. The window has to cover noticing a row vanished, reading the line
// that replaced it, and reaching for the mouse — 5s is short for that, and an undo
// that expires before it is seen is the same as no undo. Chosen on those merits;
// it also happens to be catchable in a test harness, which is a side effect and
// was not the reason.
const UNDO_MS = 8000;

const CheckCheck = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M1 13l4 4L13 9" /><path d="M9 13l4 4L23 5" /></svg>
);
const Close = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
);

// Grouped from the VIEWER'S LOCAL DATE, not UTC — a notification at 01:00 local
// belongs to today.
function dayBucket(iso) {
  const d = new Date(iso);
  const today = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(today) - startOf(d)) / 86400000);
  if (diff <= 0) return 'TODAY';
  if (diff === 1) return 'YESTERDAY';
  return 'EARLIER';
}

export function NotificationDrawer() {
  const open = useNotificationsStore((s) => s.showDrawer);
  const setShowDrawer = useNotificationsStore((s) => s.setShowDrawer);

  const h = useNotificationHistory({ enabled: open });
  const [senders, setSenders] = useState({});
  // id -> { row, atIndex } while the undo line shows. The ROW ITSELF is kept,
  // not just a flag: archiving stamps archived_at, the list refetches without it,
  // and a row that has left the list cannot render its own undo line. Holding the
  // snapshot is what lets the line appear IN PLACE (spec §5.5).
  const [undoing, setUndoing] = useState({});
  const [cursor, setCursor] = useState(0);
  const seenDone = useRef(false);
  const panelRef = useRef(null);

  const unreadCount = h.rows.filter((r) => !r.read_at).length;   // live rows only

  // seen_at, once per opening. NEVER read_at.
  useEffect(() => {
    if (!open) { seenDone.current = false; return; }
    if (seenDone.current || h.isLoading || !h.rows.length) return;
    seenDone.current = true;
    h.markSeen(h.rows.filter((r) => !r.seen_at).map((r) => r.id));
  }, [open, h.isLoading, h.rows]);

  // Sender names come from the safe cross-user view; the base table is own-row only.
  useEffect(() => {
    if (!open) return;
    const ids = [...new Set(h.rows.map((r) => r.notifications.created_by).filter(Boolean))];
    if (!ids.length) return;
    supabase.from('user_profiles_public').select('id, display_name, email').in('id', ids)
      .then(({ data }) => {
        const m = {};
        (data || []).forEach((u) => { m[u.id] = u.display_name || (u.email ? u.email.split('@')[0] : null); });
        setSenders(m);
      });
  }, [open, h.rows]);

  // Esc closes; focus moves in on open and back to the megaphone on close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, h.rows.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
      else if (e.key === 'Enter') {
        const r = h.rows[cursor];
        if (r && !r.read_at) h.markRead(r.id);
      }
    };
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.querySelector('[data-test="header-notifications"]')?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, h.rows, cursor]);

  const close = () => setShowDrawer(false);

  const handleArchive = (id) => {
    const atIndex = h.rows.findIndex((x) => x.id === id);
    const row = h.rows[atIndex];
    if (!row) return;
    setUndoing((u) => ({ ...u, [id]: { row, atIndex } }));
    h.archive(id);
    setTimeout(() => setUndoing((u) => { const n = { ...u }; delete n[id]; return n; }), UNDO_MS);
  };

  const handleUndo = (id) => {
    setUndoing((u) => { const n = { ...u }; delete n[id]; return n; });
    h.unarchive(id);
  };

  // Rows still on screen because their undo window is open, plus the live list.
  const grouped = useMemo(() => {
    // Re-insert any row whose undo window is still open, at the position it held,
    // so the undo line replaces it in place rather than appearing at the end.
    const merged = [...h.rows];
    Object.values(undoing)
      .sort((a, b) => a.atIndex - b.atIndex)
      .forEach(({ row, atIndex }) => {
        if (!merged.some((x) => x.id === row.id)) {
          merged.splice(Math.min(atIndex, merged.length), 0, row);
        }
      });

    const out = [];
    let last = null;
    merged.forEach((r) => {
      const b = dayBucket(r.notifications.created_at);
      if (b !== last) { out.push({ kind: 'head', label: b }); last = b; }
      out.push({ kind: 'row', row: r });
    });
    return out;
  }, [h.rows, undoing]);

  if (!open) return null;

  return createPortal(
    <div className={styles.ndRoot}>
      <div className={`${styles.backdrop} ${styles.backdropOpen}`} onClick={close} data-test="notification-drawer-backdrop" />
      <aside
        ref={panelRef}
        tabIndex={-1}
        className={`${styles.drawer} ${styles.drawerOpen}`}
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        data-test="notification-drawer"
      >
        <div className={styles.head}>
          <span className={styles.title}>Notifications</span>
          {/* hidden entirely at zero — never a pill containing 0 */}
          {unreadCount > 0 && (
            <span className={styles.pill} data-test="notification-drawer-pill">{unreadCount}</span>
          )}
          <div className={styles.headBtns}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => h.markAllRead()}
              disabled={unreadCount === 0}
              title={unreadCount === 0 ? 'Nothing unread' : 'Mark all read'}
              aria-label="Mark all read"
              data-test="notification-mark-all-read"
            ><CheckCheck /></button>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={close}
              aria-label="Close notifications"
              data-test="notification-drawer-close"
            ><Close /></button>
          </div>
        </div>
        <BriefingEntry onBefore={close} />

        <div className={styles.list} role="listbox" aria-label="Notification history">
          {h.isLoading && [0, 1, 2].map((i) => (
            <div className={styles.skelRow} key={i} data-test="notification-skeleton">
              <div className={styles.skelCol}>
                <div className={`${styles.skelBar} ${styles.w60}`} />
                <div className={`${styles.skelBar} ${styles.w85}`} />
              </div>
            </div>
          ))}

          {/* A failed load must never render as "you have none" — that is quietly wrong. */}
          {!h.isLoading && h.isError && (
            <div className={styles.error} data-test="notification-drawer-error">
              Couldn't load your notifications.
              <button type="button" className={styles.retry} onClick={h.retry} data-test="notification-retry">Retry</button>
            </div>
          )}

          {!h.isLoading && !h.isError && grouped.length === 0 && (
            <div className={styles.empty} data-test="notification-drawer-empty">
              Nothing yet. Announcements and alerts will appear here.
            </div>
          )}

          {!h.isLoading && !h.isError && grouped.map((item, i) =>
            item.kind === 'head'
              ? <div className={styles.dayHead} key={`h-${item.label}-${i}`}>{item.label}</div>
              : undoing[item.row.id]
                ? <ArchivedUndoRow key={item.row.id} onUndo={() => handleUndo(item.row.id)} />
                : <NotificationRow
                    key={item.row.id}
                    row={item.row}
                    senderName={senders[item.row.notifications.created_by]}
                    isActive={h.rows[cursor]?.id === item.row.id}
                    onRead={h.markRead}
                    onArchive={handleArchive}
                    onNavigate={close}
                  />
          )}

          {!h.isLoading && !h.isError && h.hasMore && (
            <button
              type="button"
              className={styles.loadOlder}
              onClick={h.loadOlder}
              disabled={h.loadingMore}
              data-test="notification-load-older"
            >
              {h.loadingMore ? 'Loading…' : 'Load older'}
            </button>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

export default NotificationDrawer;
